import {
  LeftOutlined,
  PrinterOutlined,
  RightOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { App, Button, Form, Input, Modal, Result, Space, Tag } from 'antd';
import {
  type CSSProperties,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { HoverImagePreview } from '../../../shared/HoverImagePreview';
import type {
  CaseShowcaseShareCaseSnapshot,
  CaseShowcaseShareSnapshot,
} from '../mock/caseShowcaseShare';
import { formatMoney } from '../mock/catalog';
import styles from '../styles.module.css';

type CustomerDecision = 'accept' | 'request_change';
type ShareItem = CaseShowcaseShareCaseSnapshot['items'][number];

const equipmentCategoryNames: Record<string, string> = {
  摄: '摄影设备',
  镜: '镜头',
  灯: '灯光设备',
  收: '收音设备',
  采: '采集与导播',
  电: '电脑与支撑',
};

interface CustomerFormErrors {
  decision?: string;
  changeRequest?: string;
  name?: string;
  phone?: string;
}

const CUSTOMER_DECISION_ERROR = '请选择本轮意见';
const CUSTOMER_CHANGE_REQUEST_ERROR = '选择「要求修改」时请填写修改意见';

interface CustomerConfirmation {
  decision: CustomerDecision;
  name: string;
  phone: string;
  changeRequest: string;
  note: string;
  caseName: string;
  quoteNo: string;
  total: number;
}

interface CustomerFeedbackDraft {
  decision: CustomerDecision | null;
  name: string;
  phone: string;
  changeRequest: string;
  note: string;
  remarks: Record<string, string>;
  formErrors: CustomerFormErrors;
  showFormErrors: boolean;
  submitted: CustomerConfirmation | null;
}

const emptyCustomerFeedback: CustomerFeedbackDraft = {
  decision: null,
  name: '',
  phone: '',
  changeRequest: '',
  note: '',
  remarks: {},
  formErrors: {},
  showFormErrors: false,
  submitted: null,
};

export function CaseCustomerSharePage({
  snapshot,
}: {
  snapshot: CaseShowcaseShareSnapshot | null;
}) {
  const { message } = App.useApp();
  const [feedbackByShareCase, setFeedbackByShareCase] = useState<
    Record<string, CustomerFeedbackDraft>
  >({});
  const [remarkScopeKey, setRemarkScopeKey] = useState<string | null>(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [compactCover, setCompactCover] = useState(
    () => window.matchMedia('(max-width: 840px)').matches,
  );
  const featureCardRef = useRef<HTMLElement>(null);
  const cardSurfaceRef = useRef<HTMLButtonElement>(null);
  const swipeRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    width: number;
    axis: 'x' | 'y' | null;
  } | null>(null);
  const suppressCardClick = useRef(false);
  const sharedCases = snapshot?.cases ?? [];
  const activeShareCase = sharedCases[activeCaseIndex] ?? sharedCases[0];

  useEffect(() => {
    const media = window.matchMedia('(max-width: 840px)');
    const updateCompactCover = () => setCompactCover(media.matches);
    media.addEventListener('change', updateCompactCover);
    return () => media.removeEventListener('change', updateCompactCover);
  }, []);

  const returnToHome = () => {
    window.location.hash = '';
  };

  if (!snapshot || !activeShareCase) {
    return (
      <Result
        status="warning"
        title="案例演练分享链接无效或已过期"
        subTitle="请返回案例库重新生成分享链接。"
        extra={
          <Button icon={<LeftOutlined />} onClick={returnToHome}>
            返回系统首页
          </Button>
        }
      />
    );
  }

  const displayedCase = activeShareCase.case;
  const displayedPlan = activeShareCase.plan;
  const displayedItems: ShareItem[] = activeShareCase.items;
  const displayedQuoteNo = activeShareCase.quoteNo;
  const displayedTotal = displayedItems.reduce(
    (amount, item) => amount + item.unitPrice * item.quantity,
    0,
  );
  const equipmentGroups = displayedItems.reduce<
    Array<{ name: string; items: ShareItem[] }>
  >((groups, item) => {
    const categoryCode = /^SKU-([^\d])/.exec(item.sku)?.[1] ?? '';
    const name = equipmentCategoryNames[categoryCode] ?? '其他设备';
    const group = groups.find((entry) => entry.name === name);
    if (group) group.items.push(item);
    else groups.push({ name, items: [item] });
    return groups;
  }, []);
  const cartTotal = sharedCases.reduce(
    (total, caseSnapshot) =>
      total +
      caseSnapshot.items.reduce(
        (caseTotal, item) => caseTotal + item.unitPrice * item.quantity,
        0,
      ),
    0,
  );
  const shouldLoopCarousel = sharedCases.length >= 3;
  const previousCaseIndex =
    activeCaseIndex > 0
      ? activeCaseIndex - 1
      : shouldLoopCarousel
        ? sharedCases.length - 1
        : undefined;
  const nextCaseIndex =
    activeCaseIndex < sharedCases.length - 1
      ? activeCaseIndex + 1
      : shouldLoopCarousel
        ? 0
        : undefined;
  const previewCount = Math.min(sharedCases.length - 1, 6);
  const previousPreviewCount =
    sharedCases.length === 2 ? activeCaseIndex : Math.floor(previewCount / 2);
  const feedbackKey = `${snapshot.token}:${displayedCase.caseId}`;
  const customerFeedback =
    feedbackByShareCase[feedbackKey] ?? emptyCustomerFeedback;
  const updateCustomerFeedback = (
    update: (current: CustomerFeedbackDraft) => CustomerFeedbackDraft,
  ) => {
    setFeedbackByShareCase((current) => {
      const currentCaseFeedback = current[feedbackKey] ?? emptyCustomerFeedback;
      return {
        ...current,
        [feedbackKey]: update(currentCaseFeedback),
      };
    });
  };
  const getRemarkScopeKey = (deviceId: string) =>
    `case:${displayedCase.caseId}:${deviceId}`;

  const selectCase = (
    index: number,
    direction = index > activeCaseIndex ? 1 : -1,
  ) => {
    if (index === activeCaseIndex) return;
    setRemarkScopeKey(null);
    setSlideDirection(direction);
    setActiveCaseIndex(index);
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  };
  const scrollToDetails = () => scrollToSection('customer-quote-content');

  const resetSwipe = () => {
    swipeRef.current = null;
    const card = featureCardRef.current;
    card?.removeAttribute('data-dragging');
    card?.style.removeProperty('--card-drag-x');
    card?.style.removeProperty('--card-drag-rotation');
  };
  const startSwipe = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0) return;
    suppressCardClick.current = false;
    swipeRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: event.currentTarget.getBoundingClientRect().width,
      axis: null,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveSwipe = (event: PointerEvent<HTMLButtonElement>) => {
    const swipe = swipeRef.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    if (!swipe.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
      swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      suppressCardClick.current = true;
    }
    if (swipe.axis !== 'x' || sharedCases.length < 2) return;
    const atEdge =
      dx > 0 ? previousCaseIndex === undefined : nextCaseIndex === undefined;
    const offset = dx * (atEdge ? 0.16 : 0.85);
    const card = featureCardRef.current;
    card?.setAttribute('data-dragging', 'true');
    card?.style.setProperty('--card-drag-x', `${offset}px`);
    card?.style.setProperty(
      '--card-drag-rotation',
      `${Math.max(-4, Math.min(4, dx / 70))}deg`,
    );
  };
  const endSwipe = (event: PointerEvent<HTMLButtonElement>) => {
    const swipe = swipeRef.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    const dx = event.clientX - swipe.x;
    const targetIndex = dx < 0 ? nextCaseIndex : previousCaseIndex;
    const committed =
      swipe.axis === 'x' &&
      Math.abs(dx) >= Math.max(48, Math.min(100, swipe.width * 0.16));
    resetSwipe();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (committed && targetIndex !== undefined) {
      selectCase(targetIndex, dx < 0 ? 1 : -1);
    }
  };

  const submitConfirmation = () => {
    const errors: CustomerFormErrors = {};
    if (!customerFeedback.decision) {
      errors.decision = CUSTOMER_DECISION_ERROR;
    }
    if (
      customerFeedback.decision === 'request_change' &&
      !customerFeedback.changeRequest.trim()
    ) {
      errors.changeRequest = CUSTOMER_CHANGE_REQUEST_ERROR;
    }
    if (!customerFeedback.name.trim()) errors.name = '请填写公司或姓名';
    if (!/^1\d{10}$/.test(customerFeedback.phone.trim())) {
      errors.phone = '请输入有效的 11 位手机号';
    }
    updateCustomerFeedback((current) => ({
      ...current,
      formErrors: errors,
      showFormErrors: true,
    }));
    if (Object.keys(errors).length) {
      message.error('请先修正必填信息');
      return;
    }

    updateCustomerFeedback((current) => ({
      ...current,
      submitted: current.decision
        ? {
            decision: current.decision,
            name: current.name.trim(),
            phone: current.phone.trim(),
            changeRequest: current.changeRequest.trim(),
            note: current.note.trim(),
            caseName: displayedCase.name,
            quoteNo: displayedQuoteNo,
            total: displayedTotal,
          }
        : null,
    }));
    message.success('案例 Mock 客户确认已在当前页面完成');
  };

  const renderPreviewStack = (position: 'previous' | 'next', count: number) => (
    <div
      className={styles.customer44PreviewStack}
      data-position={position}
      inert={compactCover}
      aria-hidden={compactCover || undefined}
      style={{ '--preview-count': count } as CSSProperties}
    >
      {Array.from({ length: count }, (_, offset) => {
        const depth = offset + 1;
        const targetIndex =
          (activeCaseIndex +
            (position === 'previous' ? -depth : depth) +
            sharedCases.length) %
          sharedCases.length;
        const previewCase = sharedCases[targetIndex].case;
        const label =
          depth === 1
            ? position === 'previous'
              ? '查看上一个案例'
              : '查看下一个案例'
            : '查看案例';
        return (
          <button
            key={previewCase.caseId}
            type="button"
            className={styles.customer44SideCard}
            data-depth={depth}
            style={{ '--preview-depth': depth } as CSSProperties}
            onClick={() => {
              selectCase(targetIndex, position === 'next' ? 1 : -1);
              cardSurfaceRef.current?.focus({ preventScroll: true });
            }}
            aria-label={`${label}：${previewCase.name}`}
            title={previewCase.name}
          >
            <img
              src={previewCase.heroImage}
              alt=""
              decoding="async"
              draggable={false}
            />
            <span className={styles.customer44SideShade} />
            <span className={styles.customer44SideNumber} aria-hidden="true">
              {String(targetIndex + 1).padStart(2, '0')}
            </span>
            {depth === 1 ? (
              <span className={styles.customer44SideCaption}>
                <small>
                  {position === 'previous' ? '上一个案例' : '下一个案例'}
                </small>
                <b>{previewCase.name}</b>
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      className={`${styles.sharePage} ${styles.customer44Page}`}
      data-case-showcase-share={snapshot.shareType === 'cart' ? 'v2' : 'v1'}
      data-case-share-state={
        snapshot.shareType === 'cart' ? 'cart-snapshot' : 'single-snapshot'
      }
    >
      <section
        id="customer-case-cover"
        className={styles.customer44Hero}
        data-multiple-cases={sharedCases.length > 1}
        aria-label={
          snapshot.shareType === 'cart'
            ? '购物车案例选片'
            : '案例效果与报价识别'
        }
      >
        <header className={styles.customer44Top}>
          <div className={styles.customer44TopIdentity}>
            <span>
              {snapshot.shareType === 'cart' ? '购物车分享' : '案例分享'}
            </span>
            <b>
              {snapshot.shareType === 'cart'
                ? `${sharedCases.length} 个案例`
                : `${displayedQuoteNo} · V${snapshot.quoteVersion}`}
            </b>
          </div>
          <div className={styles.customer44TopActions}>
            <span className={styles.customer44Expiry}>
              {snapshot.shareType === 'cart'
                ? `购物车案例 · 有效至 ${snapshot.validUntil}`
                : `当前方案 · 有效至 ${snapshot.validUntil}`}
            </span>
            <Button
              size="small"
              icon={<PrinterOutlined aria-hidden="true" />}
              onClick={() => window.print()}
            >
              打印 / 保存 PDF
            </Button>
          </div>
        </header>

        <div className={styles.customer44Carousel}>
          {renderPreviewStack('previous', previousPreviewCount)}
          <article
            ref={featureCardRef}
            className={styles.customer44FeatureCard}
            aria-label={`当前案例：${displayedCase.name}`}
            style={{ '--slide-direction': slideDirection } as CSSProperties}
          >
            <div
              key={displayedCase.caseId}
              className={styles.customer44CardVisual}
            >
              <img
                className={styles.customer44Image}
                src={displayedCase.heroImage}
                alt={`${displayedCase.name} 案例主视觉`}
                decoding="async"
                draggable={false}
              />
              <div className={styles.customer44Caption}>
                <span>
                  {displayedCase.caseCode} · 案例 {activeCaseIndex + 1} /{' '}
                  {sharedCases.length}
                </span>
                <h1>{displayedCase.name}</h1>
                <p>案例效果与方案设备明细，供本轮确认参考。</p>
                <small className={styles.customer44CardHint}>
                  {sharedCases.length > 1 ? '左右滑动切换 · ' : ''}轻点查看明细
                </small>
              </div>
            </div>
            <button
              ref={cardSurfaceRef}
              type="button"
              className={styles.customer44CardSurface}
              aria-label={`查看${displayedCase.name}方案与设备明细`}
              aria-describedby="customer44-gesture-help"
              onPointerDown={startSwipe}
              onPointerMove={moveSwipe}
              onPointerUp={endSwipe}
              onPointerCancel={() => {
                suppressCardClick.current = true;
                resetSwipe();
              }}
              onLostPointerCapture={resetSwipe}
              onClick={(event) => {
                if (!suppressCardClick.current || event.detail === 0)
                  scrollToDetails();
              }}
              onKeyDown={(event) => {
                const target =
                  event.key === 'ArrowLeft'
                    ? previousCaseIndex
                    : event.key === 'ArrowRight'
                      ? nextCaseIndex
                      : undefined;
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                  event.preventDefault();
                  if (target !== undefined)
                    selectCase(target, event.key === 'ArrowRight' ? 1 : -1);
                }
              }}
            />
          </article>
          {renderPreviewStack('next', previewCount - previousPreviewCount)}
        </div>
        <span
          id="customer44-gesture-help"
          className={styles.customer44ScreenReaderOnly}
        >
          左右拖动或按左右方向键切换案例，按回车查看当前案例明细。
        </span>
        <span
          className={styles.customer44ScreenReaderOnly}
          aria-live="polite"
          aria-atomic="true"
        >
          案例 {activeCaseIndex + 1} / {sharedCases.length}：
          {displayedCase.name}
        </span>

        <div className={styles.customer44HeroFoot}>
          <span>
            {snapshot.shareType === 'cart'
              ? `整车合计 ${formatMoney(cartTotal)}`
              : `方案金额 ${formatMoney(displayedTotal)}`}
          </span>
          <span className={styles.customer44CaseProgress} aria-hidden="true">
            <b>{String(activeCaseIndex + 1).padStart(2, '0')}</b>
            <span>/ {String(sharedCases.length).padStart(2, '0')}</span>
          </span>
        </div>
      </section>

      <main className={styles.customer44Details} id="customer-quote-content">
        <div className={styles.customer44ContentFrame}>
          <div className={styles.customer44DetailToolbar}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => scrollToSection('customer-case-cover')}
            >
              返回案例封面
            </Button>
            <span>案例分享 · 有效至 {snapshot.validUntil}</span>
          </div>

          {snapshot.shareType === 'cart' ? (
            <div className={styles.customer44CasePicker}>
              <div className={styles.customer44CasePickerHeading}>
                <b>案例目录</b>
                <span>{sharedCases.length} 个案例</span>
              </div>
              <nav
                className={styles.customer44CaseNav}
                aria-label="购物车案例明细"
              >
                {sharedCases.map((caseSnapshot, index) => (
                  <Button
                    key={caseSnapshot.case.caseId}
                    size="small"
                    type={activeCaseIndex === index ? 'primary' : 'default'}
                    aria-pressed={activeCaseIndex === index}
                    onClick={() => selectCase(index)}
                  >
                    {caseSnapshot.case.name}
                  </Button>
                ))}
              </nav>
              <small>选择案例查看对应方案与设备明细。</small>
            </div>
          ) : null}

          <header className={styles.customer44DetailHeading}>
            <div>
              <span>
                {displayedCase.caseCode} · 案例 V{displayedCase.caseVersion}
              </span>
              <h2>
                {snapshot.shareType === 'cart'
                  ? '案例设备明细'
                  : '设备报价清单'}
              </h2>
              <p>
                {snapshot.shareType === 'cart'
                  ? `案例 ${activeCaseIndex + 1} / ${sharedCases.length}`
                  : `报价编号：${displayedQuoteNo} V${snapshot.quoteVersion}`}
              </p>
            </div>
            <div className={styles.customer44DetailTotal}>
              <small>当前案例金额</small>
              <strong>{formatMoney(displayedTotal)}</strong>
            </div>
          </header>

          <div className={styles.customer44DetailGrid}>
            <section
              className={styles.customer44Package}
              aria-labelledby="customer44-plan-heading"
            >
              <div className={styles.customer44SectionEyebrow}>方案概览</div>
              <h3 id="customer44-plan-heading">{displayedPlan.name}</h3>
              {displayedPlan.name !== displayedCase.name ? (
                <span className={styles.customer44PlanCase}>
                  {displayedCase.name}
                </span>
              ) : null}
              <Tag className={styles.customer44DemoTag}>本地演练</Tag>
            </section>

            <section
              className={styles.customer44EquipmentPanel}
              aria-labelledby="customer44-equipment-heading"
            >
              <header className={styles.customer44EquipmentHeader}>
                <div>
                  <h3 id="customer44-equipment-heading">设备清单</h3>
                  <span>按当前案例快照展示</span>
                </div>
                <b>{displayedItems.length} 项设备</b>
              </header>
              {equipmentGroups.length === 0 ? (
                <p className={styles.customer44EmptyEquipment}>
                  当前案例暂无设备明细
                </p>
              ) : (
                <section
                  className={styles.customer44GroupTableViewport}
                  aria-label="设备明细表，可横向滚动"
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: 允许键盘用户横向滚动设备明细表。
                  tabIndex={0}
                >
                  <table className={styles.customer44GroupTable}>
                    <caption className={styles.customer44VisuallyHidden}>
                      {displayedCase.name}的设备品类、设备、数量和价格
                    </caption>
                    <colgroup>
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '40%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '17%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">品类</th>
                        <th scope="col">设备</th>
                        <th
                          scope="col"
                          className={styles.customer44NumericCell}
                        >
                          数量
                        </th>
                        <th
                          scope="col"
                          className={styles.customer44NumericCell}
                        >
                          单价
                        </th>
                        <th
                          scope="col"
                          className={styles.customer44NumericCell}
                        >
                          小计
                        </th>
                      </tr>
                    </thead>
                    {equipmentGroups.map((group) => (
                      <tbody key={group.name}>
                        {group.items.map((item, index) => (
                          <tr key={`${displayedCase.caseId}:${item.deviceId}`}>
                            {index === 0 ? (
                              <th
                                scope="rowgroup"
                                rowSpan={group.items.length}
                                className={styles.customer44CategoryCell}
                              >
                                <b>{group.name}</b>
                                <small>
                                  {group.items.length} 项设备 ·{' '}
                                  {formatMoney(
                                    group.items.reduce(
                                      (total, entry) =>
                                        total +
                                        entry.unitPrice * entry.quantity,
                                      0,
                                    ),
                                  )}
                                </small>
                              </th>
                            ) : null}
                            <td>
                              <span className={styles.customer44GroupDevice}>
                                <HoverImagePreview
                                  sources={[item.image]}
                                  alt={`${item.name} 图片`}
                                  width={26}
                                  height={26}
                                  style={{ borderRadius: 3 }}
                                />
                                <span>
                                  <b>{item.name}</b>
                                </span>
                              </span>
                            </td>
                            <td className={styles.customer44NumericCell}>
                              {item.quantity}
                            </td>
                            <td className={styles.customer44NumericCell}>
                              {formatMoney(item.unitPrice)}
                            </td>
                            <td className={styles.customer44NumericCell}>
                              <b>
                                {formatMoney(item.unitPrice * item.quantity)}
                              </b>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ))}
                  </table>
                </section>
              )}
            </section>
          </div>
          <div className={styles.customer44DetailContinue}>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() => scrollToSection('customer-feedback')}
            >
              继续提交客户反馈
            </Button>
          </div>
        </div>
      </main>

      <section
        id="customer-feedback"
        className={styles.customer44Feedback}
        aria-labelledby="customer44-feedback-heading"
      >
        <div className={styles.customer44FeedbackInner}>
          <header className={styles.customer44FeedbackHeader}>
            <div>
              <span>提交客户确认</span>
              <h2 id="customer44-feedback-heading">提交客户反馈</h2>
              <p>选择本轮意见，并填写需要销售跟进的内容。</p>
            </div>
            <Tag className={styles.customer44DarkTag}>演练模式</Tag>
          </header>

          {customerFeedback.submitted ? (
            <div className={styles.customer44Receipt} role="status">
              <Space wrap>
                <Tag>
                  {customerFeedback.submitted.decision === 'accept'
                    ? '本轮报价已确认'
                    : '客户反馈已在本地演示中记录'}
                </Tag>
                <Tag title="仅保留在当前页面，不会写入正式服务">演示回执</Tag>
              </Space>
              <dl>
                <div>
                  <dt>案例</dt>
                  <dd>{customerFeedback.submitted.caseName}</dd>
                </div>
                <div>
                  <dt>报价编号</dt>
                  <dd>{customerFeedback.submitted.quoteNo}</dd>
                </div>
                <div>
                  <dt>报价金额</dt>
                  <dd>{formatMoney(customerFeedback.submitted.total)}</dd>
                </div>
                <div>
                  <dt>公司或姓名</dt>
                  <dd>{customerFeedback.submitted.name}</dd>
                </div>
                <div>
                  <dt>联系电话</dt>
                  <dd>{customerFeedback.submitted.phone}</dd>
                </div>
                <div>
                  <dt>本轮意见</dt>
                  <dd>
                    {customerFeedback.submitted.decision === 'accept'
                      ? '接受本轮'
                      : '要求修改'}
                  </dd>
                </div>
                <div>
                  <dt>修改意见</dt>
                  <dd>
                    {customerFeedback.submitted.changeRequest ||
                      '（无修改意见）'}
                  </dd>
                </div>
                <div>
                  <dt>备注</dt>
                  <dd>{customerFeedback.submitted.note || '（无备注）'}</dd>
                </div>
              </dl>
              <div className={styles.customer44ReceiptActions}>
                <Button
                  onClick={() => {
                    updateCustomerFeedback((current) => ({
                      ...current,
                      submitted: null,
                    }));
                    message.info('可以修改本轮意见后重新提交');
                  }}
                >
                  重新填写
                </Button>
              </div>
            </div>
          ) : (
            <>
              <fieldset className={styles.customer44DecisionFieldset}>
                <legend>本轮意见</legend>
                <div className={styles.customer44DecisionGroup}>
                  <Button
                    type={
                      customerFeedback.decision === 'accept'
                        ? 'primary'
                        : 'default'
                    }
                    aria-pressed={customerFeedback.decision === 'accept'}
                    onClick={() =>
                      updateCustomerFeedback((current) => ({
                        ...current,
                        decision: 'accept',
                      }))
                    }
                  >
                    接受本轮
                  </Button>
                  <Button
                    type={
                      customerFeedback.decision === 'request_change'
                        ? 'primary'
                        : 'default'
                    }
                    aria-pressed={
                      customerFeedback.decision === 'request_change'
                    }
                    onClick={() =>
                      updateCustomerFeedback((current) => ({
                        ...current,
                        decision: 'request_change',
                      }))
                    }
                  >
                    要求修改
                  </Button>
                </div>
                {customerFeedback.showFormErrors &&
                customerFeedback.formErrors.decision ? (
                  <p className={styles.customer44FieldError} role="alert">
                    {customerFeedback.formErrors.decision}
                  </p>
                ) : null}
              </fieldset>

              <details className={styles.customer44DeviceFeedback}>
                <summary>
                  <span>
                    <b>逐设备反馈</b>
                    <small>需要调整单项设备时展开填写备注</small>
                  </span>
                  <span>{displayedItems.length} 项设备</span>
                </summary>
                {displayedItems.length === 0 ? (
                  <p className={styles.customer44EmptyFeedback}>
                    当前案例暂无设备可添加备注。
                  </p>
                ) : (
                  <div className={styles.customer44DeviceFeedbackList}>
                    {displayedItems.map((item, index) => {
                      const scopeKey = getRemarkScopeKey(item.deviceId);
                      return (
                        <div
                          className={styles.customer44DeviceFeedbackRow}
                          key={`${displayedCase.caseId}:${item.deviceId}`}
                        >
                          <div>
                            <small>第 {index + 1} 项</small>
                            <b>{item.name}</b>
                          </div>
                          <Button
                            size="small"
                            onClick={() => {
                              setRemarkScopeKey(scopeKey);
                              setRemarkDraft(
                                customerFeedback.remarks[scopeKey] ?? '',
                              );
                            }}
                          >
                            备注{customerFeedback.remarks[scopeKey] ? ' ✓' : ''}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </details>

              <Form layout="vertical" className={styles.customer44FeedbackForm}>
                <div className={styles.customer44FormGrid}>
                  <Form.Item
                    className={styles.customer44FormItem}
                    label="公司或姓名"
                    required
                    validateStatus={
                      customerFeedback.showFormErrors &&
                      customerFeedback.formErrors.name
                        ? 'error'
                        : undefined
                    }
                    help={
                      customerFeedback.showFormErrors
                        ? customerFeedback.formErrors.name
                        : undefined
                    }
                  >
                    <Input
                      aria-label="公司或姓名"
                      autoComplete="organization"
                      aria-invalid={Boolean(customerFeedback.formErrors.name)}
                      value={customerFeedback.name}
                      onChange={(event) => {
                        const value = event.target.value;
                        updateCustomerFeedback((current) => ({
                          ...current,
                          name: value,
                          formErrors: {
                            ...current.formErrors,
                            name: undefined,
                          },
                        }));
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className={styles.customer44FormItem}
                    label="联系电话"
                    required
                    validateStatus={
                      customerFeedback.showFormErrors &&
                      customerFeedback.formErrors.phone
                        ? 'error'
                        : undefined
                    }
                    help={
                      customerFeedback.showFormErrors
                        ? customerFeedback.formErrors.phone
                        : undefined
                    }
                  >
                    <Input
                      aria-label="手机"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={11}
                      aria-invalid={Boolean(customerFeedback.formErrors.phone)}
                      value={customerFeedback.phone}
                      onChange={(event) => {
                        const value = event.target.value;
                        updateCustomerFeedback((current) => ({
                          ...current,
                          phone: value,
                          formErrors: {
                            ...current.formErrors,
                            phone: undefined,
                          },
                        }));
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className={styles.customer44FormItem}
                    label="修改意见"
                    validateStatus={
                      customerFeedback.showFormErrors &&
                      customerFeedback.formErrors.changeRequest
                        ? 'error'
                        : undefined
                    }
                    help={
                      customerFeedback.showFormErrors
                        ? customerFeedback.formErrors.changeRequest
                        : undefined
                    }
                  >
                    <Input.TextArea
                      aria-label="修改意见"
                      rows={4}
                      aria-invalid={Boolean(
                        customerFeedback.formErrors.changeRequest,
                      )}
                      value={customerFeedback.changeRequest}
                      onChange={(event) =>
                        updateCustomerFeedback((current) => ({
                          ...current,
                          changeRequest: event.target.value,
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item className={styles.customer44FormItem} label="备注">
                    <Input.TextArea
                      aria-label="客户留言"
                      rows={4}
                      value={customerFeedback.note}
                      onChange={(event) =>
                        updateCustomerFeedback((current) => ({
                          ...current,
                          note: event.target.value,
                        }))
                      }
                    />
                  </Form.Item>
                </div>
                <div className={styles.customer44FeedbackNotice}>
                  <b>本地演练说明</b>
                  <span>
                    当前填写内容只保留在本页面，不会触达销售或进入正式后台。
                  </span>
                </div>
                <div className={styles.customer44FeedbackActions}>
                  <Button onClick={scrollToDetails}>返回方案</Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={submitConfirmation}
                  >
                    提交本次确认
                  </Button>
                </div>
              </Form>
            </>
          )}
        </div>
      </section>

      <Modal
        open={remarkScopeKey !== null}
        title="设备备注"
        okText="保存"
        cancelText="取消"
        rootClassName={styles.customer44RemarkModal}
        onCancel={() => setRemarkScopeKey(null)}
        onOk={() => {
          if (remarkScopeKey) {
            updateCustomerFeedback((current) => ({
              ...current,
              remarks: {
                ...current.remarks,
                [remarkScopeKey]: remarkDraft,
              },
            }));
          }
          setRemarkScopeKey(null);
        }}
        destroyOnHidden
      >
        <Input.TextArea
          rows={5}
          value={remarkDraft}
          onChange={(event) => setRemarkDraft(event.target.value)}
          aria-label="设备备注"
        />
      </Modal>
    </div>
  );
}
